import { ValidationSummary } from "components/shared";
import { useCentralApi, useCurrentUser } from "hooks/shared";
import { IMetadataOwner, IMetadataValue } from "models/metadata";
import { useSnackbar } from '@sagecentral/core-ui';
import { GuidOrNumber } from "models/common";

export type MetadataValueGetterSignature = (id: GuidOrNumber) => Promise<IMetadataValue | null>;
export type MetadataValueSetterSignature = (id: GuidOrNumber, originalValue: any, newValue: any) => Promise<IMetadataValue | null>;

type UseWorkItemMetadataValuesResult = [
    MetadataValueGetterSignature,
    MetadataValueSetterSignature
];

type UseWorkItemMetadataValuesSignature = (owner: IMetadataOwner, draft?: boolean) => UseWorkItemMetadataValuesResult;

const useWorkItemMetadataValues: UseWorkItemMetadataValuesSignature = (owner, draft) => {
    const { displaySnackbar } = useSnackbar();
    const user = useCurrentUser();
    const fetcher = useCentralApi(user);
    var isEqual = require('lodash.isequal');

    const taskId = !draft ? owner?.taskId : undefined;
    const workItemId = owner?.workItemId;

    const getMetadata: MetadataValueGetterSignature = async (id: GuidOrNumber) => {        
        const query = taskId ? `?taskId=${taskId}` : '';
        try {
            const url = draft 
                ? `/api/v3/drafts/workitems/${workItemId}/metadata/${id}`
                : `/api/v3/workitems/metadata/${id}${query}`;
            const resp = await fetcher(url);
            if (!resp.ok) {
                return null;
            }

            return await resp.json() as IMetadataValue;
        } finally {

        }
    };

    const setMetadata: MetadataValueSetterSignature = async (id: GuidOrNumber, originalValue: any, newValue: any) => {
        const query = taskId ? '?taskId='+taskId : '';
        try {
            if (!newValue) return null;

            if ((isEqual(newValue, originalValue) || JSON.stringify(originalValue) === JSON.stringify(newValue)) ){
                return null;
            }

            const url = draft 
                ? `/api/v3/drafts/workitems/${workItemId}/metadata/${id}`
                : `/api/v3/workitems/metadata/${id}${query}`;

            const resp = await fetcher(url, {
                method: 'PATCH',
                body: JSON.stringify(newValue)
            }, async (resp) => {
                if (resp.status === 400) {
                    const data = await resp.json();
                    displaySnackbar('warning', <><ValidationSummary validationDetail={data}/></>);
                    return true;
                } else {
                    displaySnackbar('error', "We couldn't update this field. Please try again.");
                }
                return false;
            });
            if (!resp.ok) {
                return null;
            }

            displaySnackbar('success', 'Success! The metadata has been updated.');

            return await resp.json() as IMetadataValue;
        } catch (err) {
            displaySnackbar('error', "We couldn't update this field. Please try again.");
            return null;
        } finally {
            
        }
    };

    return [
        getMetadata,
        setMetadata
    ];
};

export default useWorkItemMetadataValues;